<template>
    <div class="filter-custom-container" :class="{'filter-collapsed': !showDesktopFilter && !hasActiveFilters}">
        <!-- Bouton toggle pour afficher/masquer les filtres (desktop) -->
        <div v-if="!isMobile" class="filter-toggle-container d-none d-md-flex justify-content-end mb-1">
            <button class="btn btn-sm btn-outline-secondary d-flex align-items-center" @click="toggleDesktopFilter">
                <i class="fa fa-filter me-1"></i> 
                {{ showDesktopFilter ? 'Masquer les filtres' : 'Afficher les filtres' }}
                <i :class="showDesktopFilter ? 'fa fa-chevron-up ms-1' : 'fa fa-chevron-down ms-1'"></i>
            </button>
        </div>

        <!-- Mobile: bouton filtre -->
        <div v-if="isMobile" class="d-block d-md-none mb-2">
            <button class="btn btn-primary w-100 d-flex align-items-center justify-content-center"
                @click="toggleMobileFilter">
                <i class="fa fa-filter me-2"></i> Filtrer
                <i :class="showMobileFilter ? 'fa fa-chevron-up ms-2' : 'fa fa-chevron-down ms-2'"></i>
            </button>

            <!-- Formulaire mobile accordéon -->
            <div v-if="showMobileFilter" class="mobile-filter-panel card mt-2">
                <div class="card-body py-2 px-2">
                    <div class="filter-section">
                        <div v-for="(select, index) in selectOptions" :key="index"
                            class="filter-select-wrapper w-100 mb-2">
                            <label :for="'mobile-filter-' + select.name" class="form-label mb-1">{{ select.label ||
                                select.name }}</label>
                            <select :id="'mobile-filter-' + select.name" class="form-select form-control"
                                v-model="selectedValues[select.name]" @change="updateFilter">
                                <option value="">{{ select.placeholder || 'Sélectionner' }}</option>
                                <option v-for="option in select.options" :key="option.value" :value="option.value">
                                    {{ option.label }}
                                </option>
                            </select>
                        </div>

                        <!-- Filtres de date par défaut -->
                        <div v-if="hasDateFilter" class="filter-date-range w-100">
                            <div class="filter-date-wrapper w-100 mb-2">
                                <label for="mobile-date-debut" class="form-label mb-1">Date début</label>
                                <input type="date" id="mobile-date-debut" class="form-control"
                                    v-model="dateFilters.date_debut" @change="updateFilter" />
                            </div>
                            <div class="filter-date-wrapper w-100 mb-2">
                                <label for="mobile-date-fin" class="form-label mb-1">Date fin</label>
                                <input type="date" id="mobile-date-fin" class="form-control"
                                    v-model="dateFilters.date_fin" @change="updateFilter" />
                            </div>
                        </div>

                        <!-- Afficher les sélecteurs existants si spécifiés -->
                        <div class="existing-selectors-mobile w-100">
                            <slot name="existing-selectors"></slot>
                        </div>

                        <div class="filter-actions w-100 d-flex mt-3 gap-2">
                            <button type="button" class="btn btn-primary flex-grow-1" @click="applyFilter">
                                <i class="fa fa-search me-1"></i> Filtrer
                            </button>
                            <button type="button" class="btn btn-outline-secondary flex-grow-1" @click="resetFilter">
                                <i class="fa fa-refresh me-1"></i> Réinitialiser
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Desktop: formulaire inline -->
        <div v-if="!isMobile && showDesktopFilter" class="d-none d-md-flex filter-content filter-transition">
            <div class="filter-section">
                <div v-for="(select, index) in selectOptions" :key="index" class="filter-select-wrapper">
                    <label :for="'filter-' + select.name">{{ select.label || select.name }}</label>
                    <select :id="'filter-' + select.name" class="form-control" v-model="selectedValues[select.name]"
                        @change="updateFilter">
                        <option value="">{{ select.placeholder || 'Sélectionner' }}</option>
                        <option v-for="option in select.options" :key="option.value" :value="option.value">
                            {{ option.label }}
                        </option>
                    </select>
                </div>

                <!-- Filtres de date par défaut si dans existingSelectors -->
                <div v-if="hasDateFilter" class="filter-date-range">
                    <div class="filter-date-wrapper">
                        <label for="date_debut">Date début</label>
                        <input type="date" id="date_debut" class="form-control" v-model="dateFilters.date_debut"
                            @change="updateFilter" />
                    </div>
                    <div class="filter-date-wrapper">
                        <label for="date_fin">Date fin</label>
                        <input type="date" id="date_fin" class="form-control" v-model="dateFilters.date_fin"
                            @change="updateFilter" />
                    </div>
                </div>

                <!-- Afficher les sélecteurs existants si spécifiés -->
                <slot name="existing-selectors"></slot>
            </div>

            <div class="filter-actions">
                <button type="button" class="btn btn-primary" @click="applyFilter">
                    <i class="fa fa-refresh"></i> Filtrer
                </button>
                <button type="button" class="btn btn-outline-secondary" @click="resetFilter">
                    <i class="fa fa-close"></i> Réinitialiser
                </button>
            </div>
        </div>

        <!-- Bandeau de résumé des filtres actifs (desktop) -->
        <div v-if="!isMobile && !showDesktopFilter && hasActiveFilters" class="active-filters-banner d-none d-md-flex align-items-center mt-1 mb-1">
            <span class="me-2"><i class="fa fa-filter"></i> Filtres actifs:</span>
            <div class="active-filters-list d-flex flex-wrap">
                <span v-for="(value, key) in activeFiltersDisplay" :key="key" class="active-filter-pill">
                    {{ value }}
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';

export default defineComponent({
    name: 'FilterCustom',
    props: {
        existingSelectors: {
            type: Array,
            default: () => []
        },
        selectOptions: {
            type: Array,
            required: true,
            validator: (value) => {
                if (!Array.isArray(value)) return false;
                return value.every(select => {
                    return select.name && Array.isArray(select.options) &&
                        select.options.every(option => option.value !== undefined && option.label !== undefined);
                });
            }
        },
        baseUrl: {
            type: String,
            required: true
        },
        defaultValues: {
            type: Object,
            default: () => ({})
        },
        hasDateFilter: {
            type: Boolean,
            default: false
        },
        storageKey: {
            type: String,
            default: null
        },
        initiallyExpanded: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            selectedValues: {},
            filteredUrl: '',
            dateFilters: {
                date_debut: '',
                date_fin: ''
            },
            isMobile: false,
            showMobileFilter: false,
            showDesktopFilter: true,
            currentPageUrl: '',
            localStorageKey: '',
            filterStateKey: ''
        };
    },
    computed: {
        // Vérifie si des filtres sont actifs
        hasActiveFilters() {
            // Vérifie les sélecteurs
            for (const key in this.selectedValues) {
                if (this.selectedValues[key]) return true;
            }
            
            // Vérifie les filtres de date
            if (this.dateFilters.date_debut) return true;
            if (this.dateFilters.date_fin) return true;
            
            return false;
        },
        // Retourne les filtres actifs sous forme d'objets pour affichage
        activeFiltersDisplay() {
            const display = {};
            
            // Ajouter les sélecteurs
            for (const select of this.selectOptions) {
                if (this.selectedValues[select.name]) {
                    const option = select.options.find(opt => opt.value === this.selectedValues[select.name]);
                    if (option) {
                        display[select.name] = `${select.label || select.name}: ${option.label}`;
                    }
                }
            }
            
            // Ajouter les filtres de date
            if (this.hasDateFilter) {
                if (this.dateFilters.date_debut) {
                    display['date_debut'] = `Début: ${this.dateFilters.date_debut}`;
                }
                if (this.dateFilters.date_fin) {
                    display['date_fin'] = `Fin: ${this.dateFilters.date_fin}`;
                }
            }
            
            return display;
        }
    },
    created() {
        // Récupérer l'URL actuelle (sans les paramètres de requête)
        this.currentPageUrl = window.location.pathname;
        
        // Générer une clé unique pour le localStorage basée sur l'URL actuelle
        this.localStorageKey = this.storageKey || `filter_${this.currentPageUrl.replace(/\//g, '_')}`;
        this.filterStateKey = `${this.localStorageKey}_state`;

        // Initialiser avec les valeurs du localStorage ou les valeurs par défaut
        this.loadFromLocalStorage();
        
        // Charger l'état du filtre (affiché ou caché)
        const savedFilterState = localStorage.getItem(this.filterStateKey);
        if (savedFilterState !== null) {
            this.showDesktopFilter = savedFilterState === 'true';
        } else {
            // S'assurer que le filtre est toujours masqué par défaut
            this.showDesktopFilter = false;
        }
        
        // S'assurer que tous les sélecteurs ont une entrée dans selectedValues
        this.selectOptions.forEach(select => {
            if (this.selectedValues[select.name] === undefined) {
                this.selectedValues[select.name] = '';
            }
        });

        // Vérifier si le filtre de date doit être activé automatiquement
        this.checkDateFilterPresence();

        // Générer l'URL initiale
        this.updateFilter();
    },
    mounted() {
        this.checkDateFilterPresence();
        this.checkMobile();
        window.addEventListener('resize', this.checkMobile);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.checkMobile);
    },
    methods: {
        loadFromLocalStorage() {
            try {
                // Récupérer les filtres stockés pour cette URL
                const savedFilters = localStorage.getItem(this.localStorageKey);
                
                if (savedFilters) {
                    const parsedFilters = JSON.parse(savedFilters);
                    
                    // Restaurer les valeurs sélectionnées
                    this.selectedValues = { ...this.defaultValues };
                    if (parsedFilters.selectedValues) {
                        this.selectedValues = { ...this.selectedValues, ...parsedFilters.selectedValues };
                    }
                    
                    // Restaurer les filtres de date
                    if (parsedFilters.dateFilters) {
                        this.dateFilters = { ...parsedFilters.dateFilters };
                    } else {
                        // Par défaut ou en cas d'erreur, initialiser avec les valeurs par défaut
                        this.initDefaultDates();
                    }
                } else {
                    // Pas de filtres sauvegardés, initialiser avec les valeurs par défaut
                    this.selectedValues = { ...this.defaultValues };
                    this.initDefaultDates();
                }
            } catch (error) {
                console.error('Erreur lors du chargement des filtres depuis localStorage:', error);
                
                // En cas d'erreur, initialiser avec les valeurs par défaut
                this.selectedValues = { ...this.defaultValues };
                this.initDefaultDates();
            }
        },
        
        saveToLocalStorage() {
            try {
                // Sauvegarder les filtres actuels dans localStorage
                const filtersToSave = {
                    selectedValues: this.selectedValues,
                    dateFilters: this.dateFilters
                };
                
                localStorage.setItem(this.localStorageKey, JSON.stringify(filtersToSave));
                
                // Sauvegarder aussi l'état d'affichage du filtre
                localStorage.setItem(this.filterStateKey, this.showDesktopFilter.toString());
            } catch (error) {
                console.error('Erreur lors de la sauvegarde des filtres dans localStorage:', error);
            }
        },
        
        toggleDesktopFilter() {
            this.showDesktopFilter = !this.showDesktopFilter;
            localStorage.setItem(this.filterStateKey, this.showDesktopFilter.toString());
        },
        
        initDefaultDates() {
            // Initialiser les filtres de date avec les valeurs par défaut ou celles du jour actuel
            if (this.defaultValues.date_debut) {
                this.dateFilters.date_debut = this.defaultValues.date_debut;
            } else {
                this.dateFilters.date_debut = '';
            }
            
            if (this.defaultValues.date_fin) {
                this.dateFilters.date_fin = this.defaultValues.date_fin;
            } else {
                // Par défaut, date de fin = aujourd'hui
                const today = new Date();
                const yyyy = today.getFullYear();
                const mm = String(today.getMonth() + 1).padStart(2, '0');
                const dd = String(today.getDate()).padStart(2, '0');
                this.dateFilters.date_fin = ``;
            }
        },
        
        toggleMobileFilter() {
            this.showMobileFilter = !this.showMobileFilter;
        },
        
        checkMobile() {
            this.isMobile = window.innerWidth <= 770;
        },
        
        checkDateFilterPresence() {
            // Activer automatiquement le filtre de date si mentionné dans existingSelectors
            if (!this.hasDateFilter && Array.isArray(this.existingSelectors)) {
                if (this.existingSelectors.includes('date_debut') ||
                    this.existingSelectors.includes('date_fin')) {
                    this.$props.hasDateFilter = true;
                }
            }
        },
        
        updateFilter() {
            // Construire l'URL avec les paramètres de filtre
            let url = this.baseUrl;
            const params = [];

            // Ajouter les paramètres des sélecteurs personnalisés
            for (const [key, value] of Object.entries(this.selectedValues)) {
                if (value) {
                    params.push(`${key}=${encodeURIComponent(value)}`);
                }
            }

            // Ajouter les filtres de date s'ils sont activés
            if (this.hasDateFilter) {
                if (this.dateFilters.date_debut) {
                    params.push(`date_debut=${encodeURIComponent(this.dateFilters.date_debut)}`);
                }
                if (this.dateFilters.date_fin) {
                    params.push(`date_fin=${encodeURIComponent(this.dateFilters.date_fin)}`);
                }
            }

            // Récupérer les paramètres des sélecteurs existants via les événements
            this.$emit('get-existing-selectors', (existingParams) => {
                if (existingParams && Object.keys(existingParams).length > 0) {
                    for (const [key, value] of Object.entries(existingParams)) {
                        // Ne pas ajouter à nouveau les paramètres de date s'ils sont déjà gérés
                        if (this.hasDateFilter && (key === 'date_debut' || key === 'date_fin')) {
                            continue;
                        }
                        if (value) {
                            params.push(`${key}=${encodeURIComponent(value)}`);
                        }
                    }
                }
            });

            // Construire l'URL complète
            if (params.length > 0) {
                url += (url.includes('?') ? '&' : '?') + params.join('&');
            }

            this.filteredUrl = url;
        },
        
        applyFilter() {
            this.updateFilter();
            
            // Sauvegarder les filtres dans le localStorage
            this.saveToLocalStorage();
            
            // Émettre l'URL filtrée et toutes les valeurs combinées des filtres
            const allValues = {
                ...this.selectedValues,
                ...this.hasDateFilter ? this.dateFilters : {}
            };
            this.$emit('filter-applied', this.filteredUrl, allValues);
        },
        
        resetFilter() {
            // Réinitialiser toutes les valeurs sélectionnées
            this.selectOptions.forEach(select => {
                this.selectedValues[select.name] = '';
            });

            // Réinitialiser les filtres de date
            this.dateFilters.date_debut = '';

            // Date de fin = aujourd'hui
            this.dateFilters.date_fin = ``;

            // Émettre un événement pour réinitialiser les sélecteurs existants
            this.$emit('reset-existing-selectors');

            // Mettre à jour l'URL
            this.updateFilter();
            
            // Supprimer les filtres sauvegardés dans localStorage
            localStorage.removeItem(this.localStorageKey);

            // Émettre un événement de réinitialisation
            this.$emit('filter-reset', this.baseUrl);
        },
        
        // Méthode pour obtenir l'URL filtrée actuelle (peut être appelée par le parent)
        getFilteredUrl() {
            this.updateFilter();
            return this.filteredUrl;
        },
        
        // Méthode pour définir manuellement les valeurs de filtre (peut être appelée par le parent)
        setFilterValues(values) {
            // Mettre à jour les valeurs de sélecteurs
            this.selectedValues = { ...this.selectedValues, ...values };

            // Mettre à jour les filtres de date si présents
            if (values.date_debut !== undefined) {
                this.dateFilters.date_debut = values.date_debut;
            }
            if (values.date_fin !== undefined) {
                this.dateFilters.date_fin = values.date_fin;
            }

            this.updateFilter();
            
            // Sauvegarder les changements
            this.saveToLocalStorage();
        }
    }
});
</script>

<style scoped>
.filter-custom-container {
    width: 100%;
    margin-bottom: 5px;
    transition: all 0.3s ease;
}

.filter-collapsed {
    margin-bottom: 0px; /* Réduit l'espace lorsque le filtre est masqué */
}

.filter-toggle-container {
    width: 100%;
    margin-bottom: 1px; /* Réduit la marge inférieure */
}

.filter-content {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 5px;
    width: 100%;
    /* margin-bottom: 15px; */
}

.filter-transition {
    animation: slideDownDesktop 0.3s ease;
}

.filter-section {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    flex: 1;
    width: 100%;
}

.filter-select-wrapper,
.filter-date-wrapper {
    min-width: 200px;
    flex-grow: 1;
}

.filter-date-range {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
}

.filter-actions {
    display: flex;
    gap: 5px;
}

/* Styles pour le bandeau des filtres actifs */
.active-filters-banner {
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 0.25rem;
    padding: 1px 8px; /* Réduit le padding vertical */
    font-size: 0.9em;
    margin-bottom: 2px; /* Réduit la marge inférieure */
    flex-wrap: wrap;
}

.active-filters-list {
    overflow-x: auto;
    max-width: 100%;
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
}

.active-filter-pill {
    background-color: #e9ecef;
    border-radius: 16px;
    padding: 1px 8px; /* Réduit le padding */
    margin-right: 5px;
    margin-bottom: 2px; /* Réduit la marge inférieure */
    font-size: 0.85em;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
}

/* Styles mobiles */
.mobile-filter-panel {
    border: 1px solid #dee2e6;
    border-radius: 0.25rem;
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
    animation: slideDown 0.3s ease;
}

@keyframes slideDown {
    from {
        max-height: 0;
        opacity: 0;
    }

    to {
        max-height: 1000px;
        opacity: 1;
    }
}

@keyframes slideDownDesktop {
    from {
        max-height: 0;
        opacity: 0;
    }

    to {
        max-height: 500px;
        opacity: 1;
    }
}

@media (max-width: 769.98px) {
    .filter-custom-container {
        margin-bottom: 2px; /* Réduit la marge en mode mobile */
    }

    .filter-section {
        gap: 8px;
    }

    .filter-select-wrapper,
    .filter-date-wrapper {
        min-width: 100%;
    }

    .form-label {
        font-size: 0.85rem;
        margin-bottom: 0.25rem;
    }

    .form-control,
    .form-select {
        font-size: 0.9rem;
        padding: 0.25rem 0.5rem;
        height: calc(1.5em + 0.5rem + 2px);
    }

    /* Correction pour les slots existants */
    .existing-selectors-mobile>div {
        width: 100%;
        margin-bottom: 8px;
    }

    .existing-selectors-mobile label {
        font-size: 0.85rem;
        margin-bottom: 0.25rem;
    }
}

/* Ajout de breakpoints supplémentaires pour les petits écrans */
@media (max-width: 575.98px) {
    .filter-toggle-container button {
        font-size: 0.85rem;
        padding: 0.2rem 0.5rem;
    }
    
    .filter-actions {
        flex-direction: column;
        width: 100%;
    }
    
    .filter-actions button {
        width: 100%;
    }
    
    .active-filters-banner {
        padding: 5px;
        flex-direction: column;
        align-items: flex-start;
    }
    
    .active-filters-list {
        margin-top: 5px;
        width: 100%;
    }
    
    .active-filter-pill {
        margin-bottom: 5px;
        font-size: 0.8em;
    }
    
    .mobile-filter-panel .filter-actions {
        flex-direction: column;
    }
}

/* Ajustements pour les écrans très petits */
@media (max-width: 359.98px) {
    .form-control,
    .form-select {
        font-size: 0.85rem;
    }
    
    .form-label {
        font-size: 0.8rem;
    }
    
    .filter-actions button {
        padding: 0.25rem 0.5rem;
        font-size: 0.85rem;
    }
}
</style>